import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFinanceOwners, getFlights } from '../../api';
import type { ExpenseCostSplit, ExpenseOwnerShare } from '../../api';
import type { Flight } from '../../types';
import { DateFormat } from '../../utils/DateFormat';
import { flightDurationMh, flightMotohoursEnd, flightTachValue, MH_UNIT } from '../../utils/flightDisplay';
import {
  EXPENSE_SPLIT_OPTIONS,
  splitsForExpenseType,
} from '../../utils/expenseTypes';
import {
  formatExpenseRub,
  hoursPreviewRows,
  sharePreviewRows,
  toggleFlightId,
  weightedPreviewRows,
  type HourFlight,
} from '../../utils/expenseSplit';
import ManualShareEditor from '../ManualShareEditor/ManualShareEditor';
import '../AddExpenseModal/AddExpenseModal.css';

type PayerOption = { id: number; name: string };

type Props = {
  date: string;
  amount: number | null;
  expenseType?: string | null;
  costSplit: ExpenseCostSplit;
  manualShares: Record<number, string>;
  flightIds: number[];
  onCostSplitChange: (value: ExpenseCostSplit) => void;
  onManualSharesChange: (value: Record<number, string>) => void;
  onFlightIdsChange: (value: number[]) => void;
  radioName?: string;
  payerUserId?: number;
  onPayerUserIdChange?: (value: number) => void;
  payerOptions?: PayerOption[];
  lockedHourFlight?: HourFlight & { label?: string } | null;
  allowedSplits?: ExpenseCostSplit[];
};

const flightLabel = (flight: Flight) =>
  `${DateFormat(String(flight.date))} · ${flight.pilot} · Моточасы ${flightMotohoursEnd(flight)} · Тахометр ${flightTachValue(flight)} · ${flightDurationMh(flight)} ${MH_UNIT}`;

const ExpenseSplitFields = ({
  date,
  amount,
  expenseType,
  costSplit,
  manualShares,
  flightIds,
  onCostSplitChange,
  onManualSharesChange,
  onFlightIdsChange,
  radioName = 'expense-cost-split',
  payerUserId = 0,
  onPayerUserIdChange,
  payerOptions = [],
  lockedHourFlight = null,
  allowedSplits,
}: Props) => {
  const [owners, setOwners] = useState<ExpenseOwnerShare[]>([]);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [flightsLoading, setFlightsLoading] = useState(false);
  const ownersReqId = useRef(0);
  const flightsReqId = useRef(0);
  const allowed = allowedSplits || splitsForExpenseType(expenseType);
  const options = EXPENSE_SPLIT_OPTIONS.filter((item) =>
    allowed.includes(item.value)
  );
  const splitHint =
    EXPENSE_SPLIT_OPTIONS.find((item) => item.value === costSplit)?.hint || '';
  const hourFlights = useMemo(() => {
    if (!lockedHourFlight) return flights;
    return [
      lockedHourFlight,
      ...flights.filter((item) => item.id !== lockedHourFlight.id),
    ];
  }, [flights, lockedHourFlight]);
  const selectedHourIds = useMemo(() => {
    if (!lockedHourFlight) return flightIds;
    return flightIds.includes(lockedHourFlight.id)
      ? flightIds
      : [lockedHourFlight.id, ...flightIds];
  }, [flightIds, lockedHourFlight]);
  const previewRows =
    costSplit === 'manual'
      ? weightedPreviewRows(owners, manualShares, amount)
      : costSplit === 'hours'
        ? hoursPreviewRows(hourFlights, selectedHourIds, amount)
        : costSplit === 'personal'
          ? payerOptions
              .filter((item) => item.id === payerUserId)
              .map((item) => ({
                key: String(item.id),
                name: item.name,
                detail: 'лично',
                cost: amount,
              }))
          : sharePreviewRows(owners, amount);
  const sortedFlights = useMemo(
    () => [...flights].sort((a, b) => flightMotohoursEnd(b) - flightMotohoursEnd(a)),
    [flights]
  );

  const loadOwners = useCallback(async (day: string) => {
    const reqId = ++ownersReqId.current;
    if (!day) {
      setOwners([]);
      return;
    }
    try {
      const res = await getFinanceOwners(day);
      if (reqId !== ownersReqId.current) return;
      setOwners(res.data.owners || []);
    } catch {
      if (reqId !== ownersReqId.current) return;
      setOwners([]);
    }
  }, []);

  const loadFlights = useCallback(async () => {
    const reqId = ++flightsReqId.current;
    setFlightsLoading(true);
    try {
      const res = await getFlights();
      if (reqId !== flightsReqId.current) return;
      setFlights(res.data || []);
    } catch {
      if (reqId !== flightsReqId.current) return;
      setFlights([]);
    }
    if (reqId !== flightsReqId.current) return;
    setFlightsLoading(false);
  }, []);

  useEffect(() => {
    void loadOwners(date);
    return () => {
      ownersReqId.current += 1;
    };
  }, [date, loadOwners]);

  useEffect(() => {
    if (costSplit !== 'hours') return;
    void loadFlights();
    return () => {
      flightsReqId.current += 1;
    };
  }, [costSplit, loadFlights]);

  return (
    <>
      <label>Распределение:</label>
      <fieldset className="expense-split">
        {options.map((option) => (
          <label
            key={option.value}
            className={option.disabled ? 'is-disabled' : undefined}
          >
            <input
              type="radio"
              name={radioName}
              checked={costSplit === option.value}
              disabled={option.disabled}
              onChange={() => onCostSplitChange(option.value)}
            />
            {option.label}
          </label>
        ))}
      </fieldset>
      {splitHint ? <p className="expense-split-hint">{splitHint}</p> : null}

      {costSplit === 'manual' ? (
        <>
          <label>Кто участвует</label>
          <ManualShareEditor
            owners={owners}
            amount={amount}
            manualShares={manualShares}
            onManualSharesChange={onManualSharesChange}
          />
        </>
      ) : null}

      {costSplit === 'personal' ? (
        <>
          <label>Кто оплатит</label>
          {payerOptions.length === 0 ? (
            <p className="expense-split-hint">Нет участников для выбора</p>
          ) : (
            <select
              value={payerUserId || ''}
              onChange={(event) =>
                onPayerUserIdChange?.(Number(event.target.value) || 0)
              }
            >
              <option value="">Выберите участника</option>
              {payerOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          )}
        </>
      ) : null}

      {costSplit === 'hours' ? (
        <>
          <label>Выбрать полёты</label>
          {flightsLoading ? (
            <p className="expense-split-hint">Загрузка полётов...</p>
          ) : sortedFlights.length === 0 && !lockedHourFlight ? (
            <p className="expense-split-hint">Нет полётов для выбора</p>
          ) : (
            <ul className="expense-flights">
              {lockedHourFlight ? (
                <li>
                  <label>
                    <input type="checkbox" checked disabled />
                    {lockedHourFlight.label || 'Этот полёт'}
                  </label>
                </li>
              ) : null}
              {sortedFlights
                .filter((flight) => flight.id !== lockedHourFlight?.id)
                .map((flight) => (
                <li key={flight.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={flightIds.includes(flight.id)}
                      onChange={(event) =>
                        onFlightIdsChange(
                          toggleFlightId(flightIds, flight.id, event.target.checked)
                        )
                      }
                    />
                    {flightLabel(flight)}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {previewRows.length > 0 ? (
        <ul className="expense-split-preview">
          {previewRows.map((row) => (
            <li key={row.key}>
              {row.name}: {row.detail}
              {row.cost != null ? ` — ${formatExpenseRub(row.cost)}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
};

export default ExpenseSplitFields;
